import { calgaryCalendarDate } from '../../../../utils/calgaryTime';
import { useEffect, useMemo, useState, useCallback } from "react";
import "./TransactionsTable.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faArrowUpRightFromSquare } from "@fortawesome/free-solid-svg-icons";
import { useParams } from "react-router-dom";
import FinancialService from "../../../../AppSettings/services/FinancialService";
import VenExpService from "../../../../AppSettings/services/VenExpService";
import OppExpService from "../../../../AppSettings/services/OppExpService";
import GenExpensesService from "../../../../AppSettings/services/GenExpensesService";
import FixBillService from "../../../../AppSettings/services/FixBillService";
import AgentsService from "../../../../AppSettings/services/AgentsService";
import normalizeToArray from "../../../../helpers/responseHelpers";
import { parseLocalDate } from "../../../../helpers/dateHelpers";
import Table from "../../../../Components/Table/Table";
import LogTransactionModal from "./Components/LogTransactionModal/LogTransactionModal";
import TransactionsExportModal from "../../../../Financial/Transactions/Components/TransactionsExportModal/TransactionsExportModal";

type TransactionCategory = "income" | "expense";

type TransactionRow = {
  id: string;
  type: string;
  amount: number;
  description: string;
  date: Date;
  employee: string;
  category: TransactionCategory;
};

interface AgentMap {
  [key: string | number]: {
    name: string;
    company?: string;
  };
}

const GeneralTransactionsTable: React.FC = () => {
  const { matterId } = useParams<{ matterId: string }>();
  const [transactions, setTransactions] = useState<TransactionRow[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [showExportModal, setShowExportModal] = useState<boolean>(false);
  const [typeFilter, setTypeFilter] = useState<string>("All types");
  const [categoryFilter, setCategoryFilter] = useState<string>("All");
  const [dateFilter, setDateFilter] = useState<string>("All time");
  const [searchQuery, setSearchQuery] = useState<string>("");

  const fetchAllData = useCallback(async () => {
      if (!matterId) return;

      try {
        setIsLoading(true);
        setError(null);

        // Fetch agents first for reference
        const agentsData = await AgentsService.getAgents();
        const agentsMap: AgentMap = normalizeToArray(agentsData).reduce((acc: AgentMap, agent: any) => {
          acc[agent.AgentID] = {
            name: `${agent.FirstName} ${agent.LastName}`,
            company: agent.Company
          };
          return acc;
        }, {} as AgentMap);

        const [
          billedTimeData,
          fixedFeesData,
          vendorData,
          operatingData,
          generalData,
          fixedBillsData,
          otherBillingData,
        ] = await Promise.all([
          FinancialService.getBilledTime(matterId),
          FinancialService.getAssignedFixedFees(matterId),
          VenExpService.getVenExpensesByMatter(matterId),
          OppExpService.getOppExpensesByMatter(matterId),
          GenExpensesService.getGenExpensesByMatter(matterId),
          FixBillService.getFixBillExpensesByMatter(matterId),
          FinancialService.getOtherBillingByMatter(matterId),
        ]);

        const formatted: TransactionRow[] = [];

        normalizeToArray(billedTimeData).forEach((item: any) => {
            formatted.push({
              id: `BT-${item.bt_id || item.id || crypto.randomUUID()}`,
              type: "Billable Time",
              amount: Number.isFinite(parseFloat(item.amount)) ? parseFloat(item.amount) : 0,
              description: `$${item.billedrate || 0}/hr - ${item.time_recorded || 'N/A'}`,
              date: item.date || item.date_recorded ? parseLocalDate(item.date || item.date_recorded) : new Date(),
              employee: item.employee_name || "Unknown",
              category: "income",
            });
        });

        normalizeToArray(fixedFeesData).forEach((fee: any) => {
            formatted.push({
              id: `FF-${fee.assignedid || fee.id || fee.fee_code || crypto.randomUUID()}`,
              type: "Fixed Fees",
              amount: Number.isFinite(parseFloat(fee.fees)) ? parseFloat(fee.fees) : 0,
              description: `${fee.fee_code || ''} - ${fee.narrative || ''}`,
              date: fee.date_added ? parseLocalDate(fee.date_added) : new Date(),
              employee: "N/A",
              category: "income",
            });
        });

        normalizeToArray(vendorData).forEach((item: any) => {
            formatted.push({
              id: `VE-${item.ven_id}`,
              type: "Vendor Services",
              amount: Number.isFinite(parseFloat(item.amount)) ? parseFloat(item.amount) : 0,
              description: item.description || "",
              date: item.date_added ? parseLocalDate(item.date_added) : new Date(),
              employee: agentsMap[item.agentid] 
                ? `${agentsMap[item.agentid].name}${agentsMap[item.agentid].company ? ` - ${agentsMap[item.agentid].company}` : ''}`
                : 'Unknown Agent',
              category: "expense",
            });
        });

        normalizeToArray(operatingData).forEach((item: any) => {
            formatted.push({
              id: `OE-${item.opp_id}`,
              type: "Operating",
              amount: Number.isFinite(parseFloat(item.amount)) ? parseFloat(item.amount) : 0,
              description: item.description || "",
              date: item.date_added ? parseLocalDate(item.date_added) : new Date(),
              employee: agentsMap[item.agentid] 
                ? `${agentsMap[item.agentid].name}${agentsMap[item.agentid].company ? ` - ${agentsMap[item.agentid].company}` : ''}`
                : 'Unknown Agent',
              category: "expense",
            });
        });

        normalizeToArray(generalData).forEach((item: any) => {
            formatted.push({
              id: `GE-${item.ge_id || item.gen_id || crypto.randomUUID()}`,
              type: "General",
              amount: Number.isFinite(parseFloat(item.amount)) ? parseFloat(item.amount) : 0,
              description: item.description || "",
              date: item.date_added ? parseLocalDate(item.date_added) : new Date(),
              employee: agentsMap[item.agentid] 
                ? `${agentsMap[item.agentid].name}${agentsMap[item.agentid].company ? ` - ${agentsMap[item.agentid].company}` : ''}`
                : 'Unknown Agent',
              category: "expense",
            });
        });

        normalizeToArray(fixedBillsData).forEach((item: any) => {
            formatted.push({
              id: `FB-${item.fbill_id || item.bill_id}`,
              type: "Fixed Bills",
              amount: Number.isFinite(parseFloat(item.amount)) ? parseFloat(item.amount) : 0,
              description: item.description || "",
              date: item.date_added ? parseLocalDate(item.date_added) : new Date(),
              employee: agentsMap[item.agentid] 
                ? `${agentsMap[item.agentid].name}${agentsMap[item.agentid].company ? ` - ${agentsMap[item.agentid].company}` : ''}`
                : 'Unknown Agent',
              category: "expense",
            });
        });

        normalizeToArray(otherBillingData).forEach((item: any) => {
            formatted.push({
              id: `OB-${item.ob_id || item.id || crypto.randomUUID()}`,
              type: "Other Billing",
              amount: Number.isFinite(parseFloat(item.amount)) ? parseFloat(item.amount) : 0,
              description: item.description || item.narrative || "",
              date: item.date_added ? parseLocalDate(item.date_added) : new Date(),
              employee: agentsMap[item.agentid]
                ? `${agentsMap[item.agentid].name}${agentsMap[item.agentid].company ? ` - ${agentsMap[item.agentid].company}` : ''}`
                : 'N/A',
              category: "income",
            });
        });

        const sorted = formatted.sort((a, b) => b.date.getTime() - a.date.getTime());
        setTransactions(sorted);
      } catch (err) {
        console.error("Error fetching transactions:", err);
        setError("Failed to fetch transactions data.");
      } finally {
        setIsLoading(false);
      }
  }, [matterId]);

  useEffect(() => {
    fetchAllData();
  }, [fetchAllData]);

  // Filter transactions: category → type → date → search (all filters apply to real data)
  const filteredTransactions = useMemo(() => {
    let filtered = [...transactions];

    if (categoryFilter === "Income") {
      filtered = filtered.filter((t) => t.category === "income");
    } else if (categoryFilter === "Expense") {
      filtered = filtered.filter((t) => t.category === "expense");
    }

    if (typeFilter !== "All types") {
      filtered = filtered.filter((t) => t.type === typeFilter);
    }

    const now = calgaryCalendarDate();
    const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
    if (dateFilter === "Last 7 Days") {
      const cutoff = todayStart - 7 * 24 * 60 * 60 * 1000;
      filtered = filtered.filter((t) => t.date.getTime() >= cutoff);
    } else if (dateFilter === "Last 90 Days") {
      const cutoff = todayStart - 90 * 24 * 60 * 60 * 1000;
      filtered = filtered.filter((t) => t.date.getTime() >= cutoff);
    } else if (dateFilter === "This Year") {
      filtered = filtered.filter((t) => t.date.getFullYear() === now.getFullYear());
    }

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase().trim();
      filtered = filtered.filter(
        (t) =>
          t.description.toLowerCase().includes(q) ||
          t.type.toLowerCase().includes(q) ||
          (t.employee && t.employee.toLowerCase().includes(q))
      );
    }

    return filtered;
  }, [transactions, categoryFilter, typeFilter, dateFilter, searchQuery]);

  const totals = useMemo(() => {
    return filteredTransactions.reduce(
      (acc, txn) => {
        if (txn.category === "income") {
          acc.inflow += txn.amount;
        } else {
          acc.outflow += txn.amount;
        }
        acc.net = acc.inflow - acc.outflow;
        return acc;
      },
      { inflow: 0, outflow: 0, net: 0 }
    );
  }, [filteredTransactions]);

  const handleAddTransaction = () => {
    setIsModalOpen(true);
  };

  const handleExport = () => {
    setShowExportModal(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
  };

  const handleTransactionSuccess = () => {
    fetchAllData();
  };

  const columns = [
    {
      header: "Category",
      render: (row: TransactionRow) => (
        <span className={`general-TT-status ${row.category}`}>
          {row.category.charAt(0).toUpperCase() + row.category.slice(1)}
        </span>
      ),
    },
    {
      header: "Type",
      render: (row: TransactionRow) => (
        <span className={`general-TT-transaction-type ${row.type.toLowerCase().replace(/\s+/g, "-")}`}>
          {row.type}
        </span>
      ),
    },
    {
      header: "Amount",
      render: (row: TransactionRow) => (
        <span className={row.category === "income" ? "general-TT-income-amount" : "general-TT-expense-amount"}>
          {row.category === "income" ? "+" : "-"}${row.amount.toFixed(2)}
        </span>
      ),
    },
    {
      header: "Description",
      accessor: "description",
    },
    {
      header: "Date",
      render: (row: TransactionRow) => row.date.toLocaleDateString(),
    },
    {
      header: "Employee/Agent",
      accessor: "employee",
    },
  ];

  return (
    <div className="general-transactions-page-shell">
      <div className="general-transactions-header">
        <div>
          <h2>Transactions</h2>
          <p className="header-sub">Track all financials for this matter.</p>
        </div>
        <div className="general-transactions-actions">
          <button className="general-ghost-btn" onClick={handleExport}>
            <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
            <span>Export</span>
          </button>
          <button className="general-primary-btn" onClick={handleAddTransaction}>
            <FontAwesomeIcon icon={faPlus} />
            <span>Add Transaction</span>
          </button>
        </div>
      </div>

      <LogTransactionModal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        onSuccess={handleTransactionSuccess}
        matterId={matterId}
      />

      <TransactionsExportModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        data={filteredTransactions}
      />

      <div className="general-transactions-filters">
        <div className="general-transactions-search">
          <input
            type="text"
            placeholder="Search transactions..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="general-transactions-search-input"
          />
        </div>
        <div className="general-transactions-filter-dropdowns">
          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            className="general-transactions-filter-select"
          >
            <option>All</option>
            <option>Income</option>
            <option>Expense</option>
          </select>
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="general-transactions-filter-select"
          >
            <option>All types</option>
            <option>Billable Time</option>
            <option>Fixed Fees</option>
            <option>Other Billing</option>
            <option>Vendor Services</option>
            <option>Operating</option>
            <option>General</option>
            <option>Fixed Bills</option>
          </select>
          <select
            value={dateFilter}
            onChange={(e) => setDateFilter(e.target.value)}
            className="general-transactions-filter-select"
          >
            <option>All time</option>
            <option>Last 7 Days</option>
            <option>Last 90 Days</option>
            <option>This Year</option>
          </select>
        </div>
      </div>

      <div className="general-transactions-cards">
        <div className="general-tx-card">
          <div className="general-tx-card-label">Total Inflow</div>
          <div className="general-tx-card-value">${totals.inflow.toFixed(2)}</div>
          <div className="general-tx-card-sub">↑ from last month</div>
        </div>
        <div className="general-tx-card">
          <div className="general-tx-card-label">Total Outflow</div>
          <div className="general-tx-card-value outflow">${totals.outflow.toFixed(2)}</div>
          <div className="general-tx-card-sub">↓ from last month</div>
        </div>
        <div className="general-tx-card">
          <div className="general-tx-card-label">Net Cashflow</div>
          <div className="general-tx-card-value">{totals.net >= 0 ? "+" : "-"}${Math.abs(totals.net).toFixed(2)}</div>
          <div className="general-tx-card-sub">vs last month</div>
        </div>
      </div>

      <div className="general-table-container">
        {isLoading ? (
          <p>Loading transactions...</p>
        ) : error ? (
          <p className="general-error-message">{error}</p>
        ) : (
          <Table
            data={filteredTransactions}
            rowKey="id"
            columns={columns as any}
            emptyMessage="No transactions found"
          />
        )}
      </div>
    </div>
  );
};

export default GeneralTransactionsTable;
